import React from "react"
import PropTypes from "prop-types"
import { CallToActionProps } from "components/blocks/callToAction"
import ClinicInfo from "components/blocks/clinicInfo"
import ClinicInsurance from "components/blocks/clinicInsurance"
import ClinicTeam from "components/blocks/clinicTeam"
import ClinicTextImage from "components/blocks/clinicTextImage"
import ClinicVideo from "components/blocks/clinicVideo"
import ClinicWhyChoose from "components/blocks/clinicWhyChoose"
import SplitSectionV2 from "components/sections/splitSectionV2"
import SplitStickySectionV2 from "components/sections/splitStickySectionV2"
import Wrapper from "components/sections/wrapper"
import COMPONENTS from "utils/componentConstants"
import {
  applyTeamExclusions,
  findAsset,
  adjustTherapyList,
  overridesFor,
  musicTherapyAt,
  offersPhysicalTherapy,
} from "utils/clinicOverrides"
import { insurerProse, insurersFor } from "utils/clinicInsurers"
import { cityOf, localiseFaqs } from "utils/clinicFaqs"
import "./clinicDetails.scss"

/**
 * The redesign's optional text + image blocks, driven by Contentful.
 *
 * A centre can have as many as it wants, none by default, and each one can say
 * something completely different. Nothing is written here on purpose: no
 * centre has decided what these are for yet, so today every page renders none.
 * Adding a Split Content section to a clinic in Contentful makes one appear.
 *
 * WHAT AN EDITOR FILLS IN
 *   Title    the intro's title, or a heading at the top of the intro text
 *   Body     the intro's remaining paragraphs
 *   Image    optional; with none, the copy runs full width
 *   Button   optional; the intro's call to action
 *   Reverse  puts the image on the right, so two blocks can alternate
 *
 * Every clinic already carries one Split Content section holding the old
 * insurance block, which the redesigned insurance band replaced. That one is
 * skipped by its heading, so it stays off the page while any new section shows.
 */
const LEGACY_INSURANCE_BLOCK = /we help you focus on the therapy experience/i

const richText = json => {
  const nodes = json?.content || []
  const read = node =>
    (node.content || []).map(child => child.value || read(child)).join("")

  return {
    heading:
      nodes.filter(n => n.nodeType.startsWith("heading")).map(read)[0] || "",
    paragraphs: nodes
      .filter(n => n.nodeType === "paragraph")
      .map(read)
      .map(t => t.trim())
      .filter(Boolean),
  }
}

const optionalBlock = section => {
  const intro = section.intro || {}
  const titleField = richText(intro.title?.text?.json)
  const bodyField = richText(intro.text?.json)

  const title =
    titleField.paragraphs.join(" ") || titleField.heading || bodyField.heading
  const body = bodyField.paragraphs.join("\n\n")
  const image = section.image?.image

  if (LEGACY_INSURANCE_BLOCK.test(`${title} ${bodyField.heading}`)) return null
  if (!title && !body && !image?.file?.url) return null

  return {
    body,
    ctaHref: intro.action?.href,
    ctaText: intro.action?.text,
    image: { alt: image?.alt || "", src: image?.file?.url },
    reverse: Boolean(section.reverse),
    title,
  }
}

const ClinicsDetails = ({
  clinic,
  closingCta,
  insuranceLogos,
  insurerLogos,
  overrideAssets,
  services,
}) => {
  const { excludeTeam, prodTeam, servicesPhoto } = overridesFor(clinic.slug)
  const music = musicTherapyAt(clinic.slug)

  // The redesigned insurance band replaces the older insurance content, so the
  // split-content block ("We Help You Focus…" / "Our Payers" with the plans
  // image) is dropped here. The doctors section renders through ClinicTeam
  // instead of the Wrapper, so it is pulled out too, leaving the FAQ.
  const allSections = clinic.sections || []
  const team = allSections.find(
    section => section.__typename === COMPONENTS.Doctors
  )
  const optionalBlocks = allSections
    .filter(section => section.__typename === COMPONENTS.SplitContent)
    .map(optionalBlock)
    .filter(Boolean)
  const sections = allSections
    .filter(
      section =>
        section.__typename !== COMPONENTS.SplitContent &&
        section.__typename !== COMPONENTS.Doctors
    )
    // The closing call to action now sits below the FAQ, so the FAQ's own
    // "Make An Appointment" button would be a second ask in a row.
    .map(section =>
      section.__typename === COMPONENTS.Faq
        ? {
            ...section,
            action: null,
            questions: localiseFaqs(
              section.questions,
              clinic.name,
              music,
              insurerProse(clinic.slug),
              offersPhysicalTherapy(clinic.slug)
            ),
          }
        : section
    )

  // The services section is shared with the homepage, which swaps its image on
  // hover. Clinic pages show one photo of that centre instead, the interior
  // shot rather than the exterior, which the hero already uses.
  const servicePhoto =
    findAsset(overrideAssets, servicesPhoto) ||
    clinic.gallery?.[1] ||
    clinic.gallery?.[0] ||
    clinic.image
  const servicesCards = (services?.cards || []).map(card => ({
    ...card,
    image: servicePhoto || card.image,
    text: {
      ...card.text,
      text: adjustTherapyList(card.text?.text, clinic.slug),
    },
  }))

  return (
    <div className="Clinics">
      {/* Center Info, then the insurance band, sit directly under the hero */}
      <ClinicInfo clinic={clinic} />
      <ClinicInsurance
        fallbackLogos={insuranceLogos}
        insurers={insurersFor(clinic.slug)}
        logoAssets={insurerLogos}
      />

      {/* Same services at every centre; the heading, lead-in and photo are local. */}
      {services?.cards?.length > 0 && (
        <SplitStickySectionV2
          cards={servicesCards}
          ctaButton={services.ctaButton}
          headingTitle={{
            heading: `Autism Therapy Services We Offer in ${clinic.name}`,
          }}
          text={`From diagnostic evaluations to ongoing care, our team of pediatricians, neurologists, and therapists work together to support your child's development and long-term success. Explore our autism services in ${cityOf(
            clinic.name
          )}.`}
        />
      )}

      <ClinicWhyChoose clinic={clinic} overrideAssets={overrideAssets} />

      {/* Optional, and only where a centre has filled one in. */}
      {optionalBlocks.map((block, index) => (
        <ClinicTextImage {...block} key={`${block.title}-${index}`} />
      ))}

      {/* Optional, and only where a centre has a video. */}
      <ClinicVideo centreName={cityOf(clinic.name)} url={clinic.videoUrl} />

      {/* A pinned production roster stands in where dev holds the wrong
          people, and can supply the section where dev has none at all. */}
      {(team || prodTeam) && (
        <ClinicTeam
          clinic={clinic}
          doctors={prodTeam || applyTeamExclusions(team.doctors, excludeTeam)}
          title={team?.title}
        />
      )}

      <div className="Clinics" id="content">
        <div className="location-wrapper-sections">
          <Wrapper wrapperSections={sections} />
        </div>
      </div>

      {/* Closing call to action, shared with the homepage */}
      {closingCta && <SplitSectionV2 {...closingCta} />}
    </div>
  )
}

ClinicsDetails.props = {
  address: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),
  cta: PropTypes.shape(CallToActionProps),
  detailedAddress: PropTypes.string,
  doctors: PropTypes.arrayOf(
    PropTypes.shape({
      department: PropTypes.string,
      details: PropTypes.shape({
        json: PropTypes.object,
      }),
      name: PropTypes.string,
      picture: PropTypes.shape({
        fluid: PropTypes.shape({
          src: PropTypes.string,
        }),
        title: PropTypes.string,
      }),
      role: PropTypes.string,
      slug: PropTypes.string,
    })
  ),
  fax: PropTypes.string,
  headline: PropTypes.shape({
    json: PropTypes.object,
  }),
  id: PropTypes.string,
  image: PropTypes.shape({
    file: PropTypes.shape({
      url: PropTypes.string,
    }),
    fluid: PropTypes.shape({
      src: PropTypes.string,
    }),
    title: PropTypes.string,
  }),
  name: PropTypes.string,
  phone: PropTypes.string,
  postalCode: PropTypes.string,
  sidebarDetails: PropTypes.shape({
    json: PropTypes.object,
  }),
  slug: PropTypes.string,
  videoUrl: PropTypes.string,
}

ClinicsDetails.propTypes = {
  clinic: PropTypes.shape(ClinicsDetails.props),
  closingCta: PropTypes.object,
  insuranceLogos: PropTypes.array,
  insurerLogos: PropTypes.array,
  overrideAssets: PropTypes.array,
  services: PropTypes.object,
}

export default ClinicsDetails
